import { useState, useCallback, useRef } from 'react';
import taxesService from '../services/taxesService';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';

/**
 * Custom hook for managing taxes data and operations
 */
const useTaxes = () => {
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Deduplication refs
  const lastSignatureRef = useRef('');
  const lastFetchTimeRef = useRef(0);
  // Track in-flight requests to avoid duplicate network calls (useful in StrictMode)
  const inFlightRef = useRef(new Set());
  // Message alerts
  const { showCreate, showUpdate, showDelete } = useMessageAlert();

  /**
   * Generate request signature for deduplication
   */
  const generateSignature = useCallback((params) => {
    return JSON.stringify(params || {});
  }, []);

  /**
   * Check if request should be deduplicated
   */
  const shouldDedupe = useCallback((signature, force) => {
    if (force) return false;
    
    const now = Date.now();
    const timeDiff = now - lastFetchTimeRef.current;
    
    return signature === lastSignatureRef.current && timeDiff < 200;
  }, []);

  /**
   * Fetch all taxes with optional filters
   */
  const fetchTaxes = useCallback(async (params = {}, { force = false } = {}) => {
    const signature = generateSignature(params);
    
    if (shouldDedupe(signature, force)) {
      return { success: true, fromCache: true };
    }

    // If an identical request is already in-flight, skip issuing another one
    if (inFlightRef.current.has(signature)) {
      return { success: true, fromCache: true };
    }

    setLoading(true);
    setError(null);
    // mark this signature as in-flight
    inFlightRef.current.add(signature);
    
    try {
      const result = await taxesService.getAllTaxes(params);
      
      if (result.success) {
        const { data } = result;
        
        // Handle different response structures
        if (data?.taxes && Array.isArray(data.taxes)) {
          setTaxes(data.taxes);
          setTotalPages(data.totalPages || Math.ceil((data.total || data.taxes.length) / (params.limit || 20)));
          setTotalCount(data.total || data.taxes.length);
        } else if (Array.isArray(data)) {
          setTaxes(data);
          setTotalPages(1);
          setTotalCount(data.length);
        } else {
          console.warn('Unexpected taxes data structure:', data);
          setTaxes([]);
          setTotalPages(0);
          setTotalCount(0);
        }
        
        lastSignatureRef.current = signature;
        lastFetchTimeRef.current = Date.now();
      } else {
        setError(result.error);
        setTaxes([]);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message || i18n.t('hookErrors.taxes.fetchList');
      setError(errorMessage);
      setTaxes([]);
      return { success: false, error: errorMessage };
    } finally {
      // clear in-flight marker
      inFlightRef.current.delete(signature);
      setLoading(false);
    }
  }, [generateSignature, shouldDedupe]);

  /**
   * Get a specific tax by ID
   */
  const getTaxById = useCallback(async (taxId) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await taxesService.getTaxById(taxId);
      
      if (!result.success) {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message || i18n.t('hookErrors.taxes.fetchOne');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new tax rate
   */
  const createTax = useCallback(async (taxData) => {
    setError(null);
    
    try {
      const result = await taxesService.createTax(taxData);
      if (result.success) {
        // Add the new tax to the local state
        setTaxes(prevTaxes => [result.data, ...prevTaxes]);
        setTotalCount(prevCount => prevCount + 1);
  // Show success message
  const taxLabel = result.data?.name || result.data?.id || '';
  showCreate(i18n.t('taxes.notifications.created', { name: taxLabel }));
      } else {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message || i18n.t('hookErrors.taxes.create');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showCreate]);

  /**
   * Update an existing tax rate
   */
  const updateTax = useCallback(async (taxId, taxData) => {
    setError(null);
    
    try {
      const result = await taxesService.updateTax(taxId, taxData);
      
      if (result.success) {
        // Update the tax in local state
        setTaxes(prevTaxes => 
          prevTaxes.map(tax => 
            tax.id === taxId ? { ...tax, ...result.data } : tax
          )
        );
  // Show success message
  const taxLabel2 = result.data?.name || taxId;
  showUpdate(i18n.t('taxes.notifications.updated', { name: taxLabel2 }));
      } else {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message || i18n.t('hookErrors.taxes.update');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showUpdate]);

  /**
   * Delete a single tax rate
   */
  const deleteTax = useCallback(async (taxId) => {
    setError(null);
    
    try {
      const result = await taxesService.deleteTax(taxId);
      
      if (result.success) {
        // Remove the tax from local state
        setTaxes(prevTaxes => prevTaxes.filter(tax => tax.id !== taxId));
        setTotalCount(prevCount => Math.max(0, prevCount - 1));
  // Show success message
  showDelete(i18n.t('taxes.notifications.deleted', { name: taxId }));
      } else {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      // Extract detail from axios error response
      const errorMessage = (() => {
        // Check if it's an axios error with response data
        if (err.response && err.response.data) {
          const data = err.response.data;
          if (typeof data === 'string') return data;
          if (data.detail) return data.detail;
          if (data.message) return data.message;
          if (data.error) return data.error;
        }
        // Fallback to error message or default
        return err.message || i18n.t('hookErrors.taxes.deleteOne');
      })();
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showDelete]);

  /**
   * Delete multiple taxes
   */
  const bulkDeleteTaxes = useCallback(async (taxIds) => {
    if (!Array.isArray(taxIds) || taxIds.length === 0) {
      return { success: false, error: i18n.t('hookErrors.noSelection.taxes') };
    }

    setError(null);
    
    try {
      const result = await taxesService.deleteTaxes(taxIds);
      
      if (result.success) {
        // Remove the deleted taxes from local state
        setTaxes(prevTaxes => 
          prevTaxes.filter(tax => !taxIds.includes(tax.id))
        );
        setTotalCount(prevCount => Math.max(0, prevCount - taxIds.length));
        // Show success message
        const deletedCount = taxIds.length;
  const bulkMsg = deletedCount > 1 ? i18n.t('taxes.notifications.bulkDeletedMultiple', { count: String(deletedCount) }) : i18n.t('taxes.notifications.bulkDeletedSingle');
  showDelete(bulkMsg);
      } else {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message || i18n.t('hookErrors.taxes.deleteMany');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showDelete]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    taxes,
    loading,
    error,
    totalPages,
    totalCount,
    
    // Actions
    fetchTaxes,
    getTaxById,
    createTax,
    updateTax,
    deleteTax,
    bulkDeleteTaxes,
    clearError,
  };
};

export default useTaxes;